import { useCallback, useEffect, useState } from "react";
import { getMyAccount } from "api/wallet";
import { getWalletActivationStatus } from "api/withdrawal";

// Fired (on window) when the customer's wallet status may have changed in this
// tab — e.g. right after self-activation — so every useWalletStatus re-checks.
export const WALLET_STATUS_CHANGED = "wallet-status-changed";

export function notifyWalletStatusChanged() {
  window.dispatchEvent(new Event(WALLET_STATUS_CHANGED));
}

/**
 * Whether the logged-in customer's wallet is active. Most customers now have
 * their wallet deactivated (see Task_logs.md 2026-07-14) — the customer portal
 * hides all wallet UI for them. `null` while loading; a fetch failure (no
 * account, network error) is treated as inactive so the UI fails closed.
 * Re-checks on WALLET_STATUS_CHANGED and when the tab regains focus (staff may
 * have turned the wallet on or off meanwhile).
 */
export default function useWalletStatus() {
  const [walletActive, setWalletActive] = useState(null);
  // Inactive wallet that the customer may self-activate (feature switched on and
  // not turned off by staff) — the sidebar then links to the activation panel.
  const [canActivate, setCanActivate] = useState(false);
  const [loading, setLoading] = useState(true);

  const check = useCallback((isCurrent = () => true) => {
    getMyAccount()
      .then(async (res) => {
        if (!isCurrent()) return;
        const active = res && res.success ? !!res.walletActive : false;
        setWalletActive(active);
        let can = false;
        if (!active) {
          try {
            const st = await getWalletActivationStatus();
            can = !!(st && st.data && st.data.enabled && !st.data.blockedByStaff);
          } catch { /* treat as not activatable */ }
        }
        if (isCurrent()) setCanActivate(can);
      })
      .catch(() => {
        if (isCurrent()) setWalletActive(false);
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
  }, []);

  useEffect(() => {
    let active = true;
    const isCurrent = () => active;
    check(isCurrent);
    const recheck = () => check(isCurrent);
    window.addEventListener(WALLET_STATUS_CHANGED, recheck);
    window.addEventListener("focus", recheck);
    return () => {
      active = false;
      window.removeEventListener(WALLET_STATUS_CHANGED, recheck);
      window.removeEventListener("focus", recheck);
    };
  }, [check]);

  return { walletActive: !!walletActive, canActivate, loading };
}
